import { CostCarrierSchema } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import { DiagnosisSelectSchema } from '@app/icare/entities/patient/schema/Patient'
import { MobilityEnum } from '@app/icare/entities/patient/schema/PatientMobility'
import { msg } from '@lingui/core/macro'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'
import f from '../field/f'
import { PatientFormCommonSchema } from './PatientFormCommon'
import { PatientFormTypeEnum } from './PatientFormType'

export const MetabolismType = {
  Diabetes: 'Diabetes',
  HeartDisease: 'HeartDisease',
  LiverDisease: 'LiverDisease',
  KidneyDisease: 'KidneyDisease',
  Dialyctic: 'Dialyctic',
  Ostomy: 'Ostomy',
  Ileostomy: 'Ileostomy',
  DrainageProbe: 'DrainageProbe',
} as const

export const SymptomsType = {
  ApetiteLoss: 'ApetiteLoss',
  Ascities: 'Ascities',
  ChewingDifficulties: 'ChewingDifficulties',
  Constipation: 'Constipation',
  Diahrrea: 'Diahrrea',
  DryMouth: 'DryMouth',
  Edema: 'Edema',
  Fatigue: 'Fatigue',
  Nausea: 'Nausea',
  SwallowingDifficulties: 'SwallowingDifficulties',
  TasteChanges: 'TasteChanges',
  Vomiting: 'Vomiting',
} as const

export const BiaMeasurementTherapyType = {
  Chemeo: 'Chemeo',
  Radiation: 'Radiation',
  Pain: 'Pain',
} as const

export const BiaMeasurementNotificationType = {
  Doctor: 'Doctor',
  Clinic: 'Clinic',
  Other: 'Other',
} as const

const MealDataSchema = z.object({
  name: z.string().nullish(),
  amountPerDay: f.number().nullish(),
  ml: f.number().nullish(),
})

export type MealData = z.infer<typeof MealDataSchema>

export const ActiveWithDescriptionSchema = z.object({
  active: z.boolean().nullish(),
  description: z.string().nullish(),
})

export type ActiveWithDescription = z.infer<typeof ActiveWithDescriptionSchema>

export const FoodIntakePerDaySchema = z.object({
  amount: f.number().nullish(),
  description: z.string().nullish(),
})

export type FoodIntakePerDay = z.infer<typeof FoodIntakePerDaySchema>

export const HealthStatus = {
  GOOD: 'GOOD',
  REDUCED: 'REDUCED',
  STRONGLY_REDUCED: 'STRONGLY_REDUCED',
}

export const BiaMeasurementFormStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`In Bearbeitung`,
  approved: msg`Fertiggestellt`,
  cancelled: msg`Storniert`,
})

export type BiaMeasurementFormStatusType = typeof BiaMeasurementFormStatusEnum.$.Type

export const BiaMeasurementFormSchema = PatientFormCommonSchema.extend({
  status: z.enum(BiaMeasurementFormStatusEnum.$.zodEnum()).default(BiaMeasurementFormStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish(),
  type: z.literal(PatientFormTypeEnum.BiaMeasurement).default(PatientFormTypeEnum.BiaMeasurement),
  form: z.object({
    current: z
      .object({
        weight: f.number().nullish(),
        height: f.number().nullish(),
      })
      .nullish(),
    weight: z
      .object({
        last: f.number().nullish(),
        lastDate: z.string().nullish(),
      })
      .nullish(),
    costCarrier: CostCarrierSchema.nullish(), // TODO: Migrate to id only Ref
    anamnesis: z
      .object({
        diagnosis: z.array(DiagnosisSelectSchema).default(() => []),
      })
      .nullish(),
    verification: z
      .object({
        verifier: z.string().nullish(),
        verifier2: z.string().nullish(),
        biaHotline: z.boolean().nullish(),
        ukm: z.boolean().nullish(),
        notifications: z
          .record(
            z.enum(BiaMeasurementNotificationType),
            z.object({
              active: z.boolean().nullish(),
              description: z.string().nullish(),
              fax: z.string().nullish(),
            }),
          )
          .nullish(),
      })
      .nullish(),
    measurement: z
      .object({
        first: z.boolean().nullish(),
        routinary: z.boolean().nullish(),
        handToHand: z.boolean().nullish(),
        phaseAngle: f.number().nullish(),
        frequencyRange: f.number().nullish(),
        resistanceR: z.string().nullish(),
        resistanceXc: z.string().nullish(),
        resistanceSume: z.string().nullish(),
        handResistance: z.string().nullish(),
        footResistance: z.string().nullish(),
      })
      .nullish(),
    therapies: z
      .record(
        z.enum(BiaMeasurementTherapyType),
        z.object({
          active: z.boolean().nullish(),
          description: z.string().nullish(),
        }),
      )
      .nullish(),
    general: z
      .object({
        healthStatus: z.enum(HealthStatus).nullish(),
        mobility: z.enum(MobilityEnum.$.zodEnum()).nullish(),
      })
      .nullish(),
    intake: z
      .object({
        food: z.coerce.boolean().nullish(),
        drinkAmount: f.number().nullish(),
        mealsPerDay: f.number().nullish(),
      })
      .nullish(),
    foodIntakePerDay: z
      .object({
        breakfast: FoodIntakePerDaySchema.nullish(),
        snackAfterBreakfast: FoodIntakePerDaySchema.nullish(),
        lunch: FoodIntakePerDaySchema.nullish(),
        snackAfterLunch: FoodIntakePerDaySchema.nullish(),
        dinner: FoodIntakePerDaySchema.nullish(),
        customMeals: z.array(FoodIntakePerDaySchema).nullish(),
      })
      .nullish(),
    meal: z
      .object({
        comment: z.string().nullish(),
        oralNutritionSupplement: z.array(MealDataSchema).nullish(),
        enteral: z.array(MealDataSchema.omit({ ml: true })).nullish(),
        parenteral: z.array(MealDataSchema.omit({ ml: true })).nullish(),
        fluidReplacement: z.array(MealDataSchema.omit({ ml: true })).nullish(),
        vitamins: z.array(MealDataSchema.omit({ ml: true, amountPerDay: true })).nullish(),
        microElements: z.array(MealDataSchema.omit({ ml: true, amountPerDay: true })).nullish(),
      })
      .nullish(),
    activity: z
      .object({
        duringWeek: ActiveWithDescriptionSchema.nullish(),
        paused: ActiveWithDescriptionSchema.extend({
          reason: z.string().nullish(),
        }).nullish(),
        stationary: ActiveWithDescriptionSchema.nullish(),
      })
      .nullish(),
    metabolism: z
      .record(
        z.enum(MetabolismType),
        z
          .object({
            active: z.boolean().nullish(),
            description: z.string().nullish(),
          })
          .nullish(),
      )
      .nullish(),
    metabolismComment: z.string().nullish(),
    symptoms: z
      .record(
        z.enum(SymptomsType),
        z
          .object({
            active: z.boolean().nullish(),
            description: z.string().nullish(),
          })
          .nullish(),
      )
      .nullish(),
    symptomsComment: z.string().nullish(),
    comment: z.string().nullish(),
  }),
})

export type BiaMeasurementForm = z.infer<typeof BiaMeasurementFormSchema>
